import { ClickAwayListener } from "@mui/material"
import styles from "./ContextMenu.module.css"
import { Icon, IconName, IconSize } from "@/app/components/icons/Icon"
import clsx from "clsx"

export type ContextMenuPlacement =
  | "bottom"
  | "bottom-left"
  | "bottom-right"
  | "top"
  | "top-left"
  | "top-right"
  | "left"
  | "right"

const PLACEMENT_CLASS: Record<ContextMenuPlacement, string> = {
  bottom: "menuPlacementBottom",
  "bottom-left": "menuPlacementBottomLeft",
  "bottom-right": "menuPlacementBottomRight",
  top: "menuPlacementTop",
  "top-left": "menuPlacementTopLeft",
  "top-right": "menuPlacementTopRight",
  left: "menuPlacementLeft",
  right: "menuPlacementRight",
}

export function ContextMenu({
  isOpen,
  onClose,
  items,
  children,
  placement = "bottom-left",
}: {
  isOpen: boolean
  onClose: () => void
  items: ContextMenuItemProps[]
  children?: React.ReactNode
  /** Where the menu opens relative to the trigger. Default: "bottom-left". */
  placement?: ContextMenuPlacement
}) {
  const placementClass = styles[PLACEMENT_CLASS[placement]]
  return (
    <ClickAwayListener onClickAway={onClose}>
      <div className={styles.container}>
        {children}
        {isOpen && (
          <div className={clsx(styles.menu, placementClass)}>
            {items.map((item) => (
              <ContextMenuItem key={item.label} {...item} />
            ))}
          </div>
        )}
      </div>
    </ClickAwayListener>
  )
}

export enum ContextMenuItemType {
  Option = "option",
  Separator = "separator",
  Header = "header",
}

export interface ContextMenuItemProps {
  contextMenuItemType: ContextMenuItemType
  label?: string
  onClick?: () => void
  icon?: IconName
  variant?: string
  disabled?: boolean
}

export function ContextMenuItem({
  contextMenuItemType,
  label,
  onClick = () => {},
  icon,
  variant = "default",
  disabled = false,
}: ContextMenuItemProps) {
  if (contextMenuItemType === ContextMenuItemType.Separator) {
    return <div className={styles.separator} />
  }

  if (contextMenuItemType === ContextMenuItemType.Header) {
    return (
      <div className={styles.header}>
        <span className={clsx("callout", styles.label)}>{label}</span>
      </div>
    )
  }

  return (
    <div
      className={clsx(
        styles.item,
        variant === "destructive" && styles.destructive,
        disabled && styles.disabled,
      )}
      onClick={disabled ? undefined : onClick}
    >
      {icon && (
        <Icon
          name={icon}
          size={IconSize.MediumSmall}
          customColor={variant === "destructive" ? "#FF0019" : undefined}
        />
      )}
      <span className={clsx("subHeadline", styles.label)}>{label}</span>
    </div>
  )
}
